import React, { useState } from 'react';
import { useSelector } from 'react-redux';
import {
  Box,
  Tooltip,
  useMediaQuery,
  useTheme,
  Icons,
  Button,
} from '@ublend-npm/aulaui-core';
import {
  LINK_ASSIGNMENTS_NO_DESTINATION,
  LINK_ASSIGNMENTS_TOOLTIP,
} from '@app/constants/texts';
import { LTI1P3_PROVIDER, QUIZZES_PROVIDER } from '@core/assignments/constants';
import { getAssignments } from '@core/assignments/selectors';
import { Assignment, ResponsiveAssignment } from '@core/assignments/types';
import { AulaState } from '@core/types/state';
import { getAssignmentProvider } from '../utils/launchAssignment';
import ConversionModalContainer from './ConversionModal.container';
import filterDestinationAssignmentsForConversion from './filterDestinationAssignmentsForConversion';

type ConversionSectionProps = {
  assignment: Assignment;
  parentAssignment?: {
    extensions?: Array<{ id?: string }>;
  };
  styles: { purpleButton: any };
};

const ConversionSection = ({
  assignment,
  parentAssignment,
  styles,
}: ConversionSectionProps) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const [isConvertingExtension, setIsConvertingExtension] = useState(false);
  const assignments = useSelector((state: AulaState) => getAssignments(state));

  const provider = getAssignmentProvider(assignment);
  const isQuizSubmission = provider === QUIZZES_PROVIDER;
  const isLti1P3Provider = provider === LTI1P3_PROVIDER;
  const hasExtensions = Boolean(
    assignment.extensions?.length ||
      parentAssignment?.extensions?.length ||
      assignment.parentAssignmentId
  );
  const groupSet = (assignment as ResponsiveAssignment).groupSet;
  const isConversionToExtensionPermitted =
    !hasExtensions && !groupSet && !assignment.isAnonymised;

  if (
    !assignment ||
    isQuizSubmission ||
    isLti1P3Provider ||
    !isConversionToExtensionPermitted
  ) {
    return null;
  }

  const availableDestinationAssignments =
    filterDestinationAssignmentsForConversion(
      (assignments as any) || [],
      assignment as any
    );
  const hasAvailableDestinationAssignments =
    availableDestinationAssignments?.length > 0;
  const tooltipTitle = hasAvailableDestinationAssignments
    ? LINK_ASSIGNMENTS_TOOLTIP
    : LINK_ASSIGNMENTS_NO_DESTINATION;
  const assignmentId = assignment.id || assignment.objectId;

  return (
    <>
      <Box component="span" sx={{ display: 'inline-flex' }}>
        <Tooltip
          placement="bottom"
          disableInteractive
          title={tooltipTitle}
          disableFocusListener={isMobile}
          disableHoverListener={isMobile}
          disableTouchListener={isMobile}
        >
          <Button
            kind="secondary"
            sx={{
              ...styles.purpleButton,
            }}
            disabled={!hasAvailableDestinationAssignments}
            aria-label={LINK_ASSIGNMENTS_TOOLTIP}
            onClick={() => setIsConvertingExtension(true)}
          >
            <Icons.CompareArrowsOutlined fontSize="small" aria-hidden />
          </Button>
        </Tooltip>
      </Box>
      {isConvertingExtension && (
        <ConversionModalContainer
          open={isConvertingExtension}
          assignmentId={assignmentId}
          onClose={(openConvertedExtension) => {
            setIsConvertingExtension(false);
            if (typeof openConvertedExtension === 'function') {
              openConvertedExtension();
            }
          }}
        />
      )}
    </>
  );
};

export default ConversionSection;
